import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const OPEN_CART_HASH = '#open-cart';
const OPEN_CART_DRAWER_FLAG = 'theme-open-cart-drawer';

/**
 * @returns {boolean}
 */
function tryOpenCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return true;
  }
  return false;
}

function maybeOpenCartDrawerAfterRedirect() {
  if (sessionStorage.getItem(OPEN_CART_DRAWER_FLAG) !== '1') return;
  sessionStorage.removeItem(OPEN_CART_DRAWER_FLAG);

  if (tryOpenCartDrawer()) return;

  customElements.whenDefined('cart-drawer-component').then(() => {
    tryOpenCartDrawer();
  });

  let attempts = 0;
  const interval = setInterval(() => {
    if (tryOpenCartDrawer() || ++attempts > 30) clearInterval(interval);
  }, 100);
}

/**
 * @returns {boolean}
 */
function shouldOpenCartFromUrl() {
  const { hash, search } = window.location;
  if (hash === OPEN_CART_HASH || hash.includes('open-cart')) return true;

  const params = new URLSearchParams(search);
  return params.get('open_cart') === '1' || params.has('open-cart');
}

function cleanOpenCartUrl() {
  const params = new URLSearchParams(window.location.search);
  const hadQuery = params.has('open_cart') || params.has('open-cart');
  params.delete('open_cart');
  params.delete('open-cart');

  const query = params.toString();
  const path = window.location.pathname + (query ? `?${query}` : '');

  if (window.location.hash.includes('open-cart') || hadQuery) {
    history.replaceState(null, '', path);
  }
}

function openCartDrawerFromUrl() {
  maybeOpenCartDrawerAfterRedirect();
  if (!shouldOpenCartFromUrl()) return;

  if (tryOpenCartDrawer()) {
    cleanOpenCartUrl();
    return;
  }

  customElements.whenDefined('cart-drawer-component').then(() => {
    if (tryOpenCartDrawer()) cleanOpenCartUrl();
  });

  let attempts = 0;
  const interval = setInterval(() => {
    if (tryOpenCartDrawer()) {
      cleanOpenCartUrl();
      clearInterval(interval);
      return;
    }
    if (++attempts > 30) clearInterval(interval);
  }, 100);
}

function initOpenCartFromUrl() {
  openCartDrawerFromUrl();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOpenCartFromUrl);
} else {
  initOpenCartFromUrl();
}

window.addEventListener('hashchange', initOpenCartFromUrl);

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href]');
  if (!link) return;

  const href = link.getAttribute('href') || '';
  if (!href.includes('#open-cart') && !href.includes('open_cart=1') && !href.includes('open-cart')) {
    return;
  }

  try {
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;

    const isHomePath = url.pathname === window.location.pathname;
    const hasOpenCart = url.hash.includes('open-cart') || url.searchParams.has('open_cart') || url.searchParams.has('open-cart');

    if (isHomePath && hasOpenCart) {
      event.preventDefault();
      if (url.hash.includes('open-cart')) {
        history.pushState(null, '', `${url.pathname}${url.search}${OPEN_CART_HASH}`);
      }
      openCartDrawerFromUrl();
    }
  } catch {
    // Ignore malformed URLs
  }
});

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    openCartDrawerFromUrl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    const dialog = this.querySelector('.cart-drawer__dialog');
    const addedCount = Number(event?.detail?.data?.itemCount || 0);
    const cartCount = Number(
      event?.detail?.resource?.item_count || event?.detail?.cart?.item_count || 0
    );
    const hasItems =
      addedCount > 0 ||
      cartCount > 0 ||
      Boolean(this.querySelector('.cart-form, .cart-items__table-row'));

    if (dialog && hasItems) {
      dialog.classList.remove('cart-drawer--empty');
      dialog.style.removeProperty('background-color');
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
   */
  closeOnInstallmentsCtaClick() {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
